import { Link } from "react-router-dom";
import {
  ArrowRight,
  Quote,
  Landmark,
  LineChart,
  Building2,
  Factory,
  Stethoscope,
  ShieldCheck,
  Workflow,
  Sparkles,
  TrendingUp,
  Compass,
  Bot,
  Scale,
} from "lucide-react";
import { site } from "../content/site";

const ICONS = {
  Landmark,
  LineChart,
  Building2,
  Factory,
  Stethoscope,
  ShieldCheck,
  Workflow,
  Sparkles,
  TrendingUp,
  Compass,
  Bot,
  Scale,
};

const Insights = () => {
  const ins = site.insights;

  return (
    <>
      {/* HERO with featured quote */}
      <section
        className="relative pt-36 pb-24 sm:pt-44 sm:pb-32 border-b border-white/10 overflow-hidden"
        data-testid="insights-hero"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-3">
              <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55">
                {ins.eyebrow}
              </p>
              <p className="mt-4 text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/40">
                {ins.hero.eyebrow}
              </p>
            </div>
            <div className="lg:col-span-9 reveal">
              <Quote
                size={44}
                className="text-white/30 mb-6"
                strokeWidth={1.25}
              />
              <blockquote className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.18] tracking-tight">
                “{ins.hero.quote}”
              </blockquote>
              <div className="mt-10 flex items-center gap-4">
                <span className="block w-12 h-px bg-white/40" />
                <p className="text-sm font-sans-modern text-white/75 tracking-wide">
                  {ins.hero.attribution}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES BY INDUSTRY */}
      <section
        className="py-24 sm:py-32 border-b border-white/10"
        data-testid="insights-use-cases"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
              {ins.useCasesEyebrow}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight">
              {ins.useCasesTitle}
            </h2>
            <p className="mt-6 text-base sm:text-lg font-sans-modern font-light text-white/65 leading-relaxed">
              {ins.useCasesIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {ins.industries.map((ind, i) => {
              const Icon = ICONS[ind.icon];
              return (
                <article
                  key={ind.name}
                  data-testid={`insights-industry-${i}`}
                  className="bg-[#711457] p-8 sm:p-10 hover:bg-[#5C0E45] transition-colors duration-300 flex flex-col"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 border border-white/25 flex items-center justify-center">
                      {Icon && (
                        <Icon
                          size={18}
                          className="text-white"
                          strokeWidth={1.5}
                        />
                      )}
                    </div>
                    <h3 className="font-serif-display text-2xl sm:text-[1.75rem] text-white leading-tight">
                      {ind.name}
                    </h3>
                  </div>

                  <ul className="mt-8 space-y-4">
                    {ind.items.map((item, j) => (
                      <li
                        key={j}
                        className="flex gap-3 text-sm font-sans-modern font-light text-white/75 leading-relaxed"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-2 shrink-0 block w-1.5 h-1.5 bg-white/70 rotate-45"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROI HIERARCHY */}
      <section
        className="py-24 sm:py-32 border-b border-white/10 bg-[#5C0E45]/40"
        data-testid="insights-roi"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
              {ins.roiEyebrow}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight">
              {ins.roiTitle}
            </h2>
            <p className="mt-6 text-base sm:text-lg font-sans-modern font-light text-white/65 leading-relaxed">
              {ins.roiIntro}
            </p>
          </div>

          <ol className="relative max-w-5xl">
            {/* vertical connector line */}
            <span
              aria-hidden="true"
              className="hidden sm:block absolute left-[23px] top-6 bottom-6 w-px bg-white/15"
            />
            {ins.roi.map((r, i) => {
              const Icon = ICONS[r.icon];
              return (
                <li
                  key={r.name}
                  data-testid={`roi-step-${i}`}
                  className="relative reveal grid grid-cols-12 gap-4 sm:gap-8 py-8 border-b border-white/10 group"
                >
                  {/* Icon node */}
                  <div className="col-span-2 sm:col-span-1 flex justify-start">
                    <div className="relative z-10 w-12 h-12 bg-[#711457] border border-white/30 flex items-center justify-center group-hover:border-white transition-colors">
                      {Icon && (
                        <Icon
                          size={18}
                          className="text-white"
                          strokeWidth={1.5}
                        />
                      )}
                    </div>
                  </div>

                  {/* Label + arrow */}
                  <div className="col-span-10 sm:col-span-5">
                    <h3 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight">
                      {r.name}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 text-xs sm:text-sm font-sans-modern text-white/80">
                      <ArrowRight size={14} className="text-white/60" />
                      <span className="italic">{r.arrow}</span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="col-span-12 sm:col-span-6">
                    <p className="text-sm sm:text-base font-sans-modern font-light text-white/70 leading-relaxed">
                      {r.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <p className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
            Where should your AI investment land on this hierarchy?
          </p>
          <p className="mt-6 max-w-xl mx-auto text-base font-sans-modern font-light text-white/65 leading-relaxed">
            We help leadership teams sequence their portfolio — first for payback, then for advantage.
          </p>
          <Link
            to="/contact"
            data-testid="insights-cta-bottom"
            className="mt-10 inline-flex items-center justify-center h-12 px-10 bg-white text-[#711457] font-sans-modern font-semibold text-sm tracking-wide hover:bg-white/90 transition-colors"
          >
            Begin a conversation
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Insights;
